import {
  Pill,
  PillCountProps,
  PillIconButtonProps,
  PillIconProps,
  PillLabelProps,
  PillProps,
  pillCountStencil,
  pillIconButtonStencil,
  pillStencil,
} from '@workday/canvas-kit-preview-react/pill';
import { buttonStencil } from '@workday/canvas-kit-react/button';
import { createComponent } from '@workday/canvas-kit-react/common';
import { systemIconStencil } from '@workday/canvas-kit-react/icon';
import { createStencil, createStyles, handleCsProp, px2rem } from '@workday/canvas-kit-styling';
import { system } from '@workday/canvas-tokens-web';
import { WcpStyles } from '@workday/wcp-tokens';

const ThemedPillCount = createComponent('span')({
  Component: ({ children, ...props }: PillCountProps, ref, Element) => (
    <Pill.Count ref={ref} as={Element} {...handleCsProp(props, [themedPillCountStencil()])}>
      {children}
    </Pill.Count>
  ),
  displayName: 'Canvas.Pill.Count',
});

const ThemedPillIcon = createComponent('span')({
  Component: (props: PillIconProps, ref, Element) => <Pill.Icon ref={ref} as={Element} {...props} />,
  displayName: 'Canvas.Pill.Icon',
});

const ThemedPillIconButton = createComponent()({
  Component: (props: PillIconButtonProps, ref) => (
    <Pill.IconButton ref={ref} {...handleCsProp(props, [themedPillIconButtonStencil()])} />
  ),
  displayName: 'Canvas.Pill.IconButton',
});

/**
 * Canvas Kit's tooltip uses `--cnvs-sys-color-text-inverse`, which flips to dark in dark mode
 * while the tooltip background stays dark — creating dark-on-dark. Use `fg.inverseException`,
 * which is always light regardless of theme, to ensure legibility on the dark tooltip background.
 */
const pillTooltipStyles = createStyles({
  color: `${WcpStyles.color.fg.inverseException} !important`,
  '& a': { color: `${WcpStyles.color.fg.inverseException} !important` },
});

const ThemedPillLabel = createComponent('span')({
  Component: ({ children, tooltipProps, ...props }: PillLabelProps, ref, Element) => (
    <Pill.Label ref={ref} as={Element} tooltipProps={{ className: pillTooltipStyles, ...tooltipProps }} {...props}>
      {children}
    </Pill.Label>
  ),
  displayName: 'Canvas.Pill.Label',
});

/**
 * Bespoke wrapper around Canvas Kit `Pill` for this app: it layers `@workday/wcp-tokens` (`WcpStyles`)
 * so pills read correctly in dark mode. Some variant CSS intentionally diverges from stock Canvas Kit
 * Pill styling where CK still leans on light-oriented system tokens or where our merge order differs.
 */
export const ThemedPill = createComponent('button')({
  Component: ({ children, ...props }: PillProps, ref, Element) => (
    <Pill
      ref={ref}
      as={Element}
      {...handleCsProp(props, [themedPillStencil({ variants: props.variant ?? 'default' })])}
    >
      {children}
    </Pill>
  ),
  displayName: 'Canvas.Pill',
  subComponents: {
    Count: ThemedPillCount,
    Icon: ThemedPillIcon,
    IconButton: ThemedPillIconButton,
    Label: ThemedPillLabel,
  },
});

/**
 * Shared read-only pill chrome: flat `buttonStencil` / icon vars, and shadow/outline off on
 * `:hover` / `:focus-visible` / `:active`. Leave border to consumer `cs` (repeat `border` in `&:hover`).
 */
const readOnlyPillButtonVars = {
  [buttonStencil.vars.background]: 'transparent',
  [buttonStencil.vars.label]: WcpStyles.color.text.default,
  [systemIconStencil.vars.color]: WcpStyles.color.icon.default,
};

const readOnlyPillInteractionChrome = {
  boxShadow: 'none',
  outline: 'none',
  ...readOnlyPillButtonVars,
};

const themedPillStencil = createStencil({
  extends: pillStencil,
  base: {},
  modifiers: {
    variants: {
      default: {
        backgroundColor: WcpStyles.color.bg.alt.default,
        span: {
          color: WcpStyles.color.text.default,
        },
        '&:hover': {
          backgroundColor: WcpStyles.color.bg.alt.strong,
          button: {
            backgroundColor: 'transparent',
          },
        },
      },
      readOnly: {
        backgroundColor: 'transparent',
        span: {
          color: WcpStyles.color.text.default,
        },
        ...readOnlyPillButtonVars,
        '&:hover, &.hover': { ...readOnlyPillInteractionChrome },
        '&:focus-visible, &.focus': { ...readOnlyPillInteractionChrome },
        '&:active, &.active': { ...readOnlyPillInteractionChrome },
      },
      removable: {
        backgroundColor: WcpStyles.color.bg.alt.default,
        span: {
          color: WcpStyles.color.text.default,
        },
        '&:hover': {
          backgroundColor: WcpStyles.color.bg.alt.strong,
          button: {
            backgroundColor: 'transparent',
          },
        },
        button: {
          backgroundColor: 'transparent',
          border: 'none',
          marginInlineStart: px2rem(-2),
          marginInlineEnd: px2rem(-7),
          padding: 0,
          height: px2rem(22),
          width: px2rem(22),
          borderRadius: system.shape.half,
          '&:hover': {
            backgroundColor: WcpStyles.color.bg.alt.stronger,
          },
        },
      },
    },
  },
});

const themedPillCountStencil = createStencil({
  extends: pillCountStencil,
  base: {
    minWidth: system.space.x6,
    width: 'auto',
  },
});

const themedPillIconButtonStencil = createStencil({
  extends: pillIconButtonStencil,
  base: {
    [systemIconStencil.vars.color]: WcpStyles.color.icon.strong,
    '&:hover': {
      [systemIconStencil.vars.color]: WcpStyles.color.icon.strong,
    },
  },
});
